import React, {useCallback, useMemo, useState} from 'react';

import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {createSearchTemplate} from 'src/openapi/admin/apis/SearchTemplateApi';
import {TemplateType} from 'src/openapi/admin/models/TemplateType';
import {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Loading from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';

type Props = {
    userId: number,
    template: UserSearchTemplate,
    templates: UserSearchTemplate[],
    onCopyDone: (newLayout: UserSearchTemplate) => void,
    onCopyFail: (error: string) => void,
    onCopyCancel: () => void,
};

export const CopiedTemplateRow = (props: Props) => {
    const {
        userId,
        template,
        templates,
        onCopyDone,
        onCopyFail,
        onCopyCancel,
    } = props;

    const apiDispatch = useAppDispatch();

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>('Layout with this name already exists');
    const [layoutName, setLayoutName] = useState(template.templateName);

    useEffectWithDeps((prev, current) => {
        if (prev !== current) {
            setLayoutName(template.templateName);
        }
    }, template.templateName);

    const changeName = useCallback<React.ChangeEventHandler<HTMLInputElement>>(event => {
        const name = event.target.value;
        setLayoutName(name);
        setError(
            templates.some(({templateName: other, searchType}) => (
                other.toLocaleLowerCase().trim() === name.toLocaleLowerCase().trim() &&
                searchType === template?.searchType
            )) ? 'Layout with this name already exists' : undefined,
        );
    }, [templates, template]);

    const confirmRename = useCallback(async () => {
        if (error === 'Layout with this name already exists' || !layoutName.trim()) {
            return;
        }

        if (template.templateName.trim() !== layoutName.trim()) {
            setBusy(true);
            const response = await apiDispatch(createSearchTemplate({
                updateTemplateRequest: {
                    userId,
                    templateName: layoutName,
                    defaultSortColumn: template.defaultSortColumn,
                    defaultSortDirection: template.defaultSortDirection,
                    searchType: template.searchType,
                    templateType: TemplateType.CUSTOM,
                    columns: template.columns,
                },
            }));
            setBusy(false);

            if (response.payload.success) {
                const newTemplateId = response.payload.data.templateId;
                const newTemplate = {
                    ...template,
                    templateName: layoutName,
                    templateId: newTemplateId,
                };

                onCopyDone(newTemplate);
            } else {
                onCopyFail(response.payload.data.message || 'Failed to create layout');
            }
        }
    }, [layoutName, template, error, apiDispatch, onCopyDone, onCopyFail, userId]);

    const endAdornment = useMemo(() => {
        if (busy) {
            return (
                <Loading type="ICON" fast={true}/>
            );
        }

        return (
            <Group alignItems="center" gap="s">
                <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                    <Icon
                        ref={anchorRef}
                        {...getAnchorProps()}
                        type={Icons.CLOSE}
                        size="Size_14"
                        onClick={onCopyCancel}
                    />
                )}>
                    Cancel
                </Tooltip>
                {error ? (
                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                        <Icon
                            className="error-color"
                            ref={anchorRef}
                            {...getAnchorProps()}
                            type={Icons.ERROR}
                        />
                    )}>
                        {error}
                    </Tooltip>
                ) : null}
                {(!error && layoutName) ? (
                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                        <Icon
                            {...getAnchorProps()}
                            ref={anchorRef}
                            type={Icons.CHECK}
                            size="Size_14"
                            onClick={confirmRename}
                        />
                    )}>
                        Submit
                    </Tooltip>
                ) : null}
            </Group>
        );
    }, [busy, error, confirmRename, layoutName, onCopyCancel]);

    return (
        <Input
            autoFocus={true}
            value={layoutName}
            color={error ? 'ERROR' : undefined}
            endAdornment={endAdornment}
            disabled={busy}
            onChange={changeName}
            maxLength={64}
            placeholder="Enter layout name"
            onKeyDown={event => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur();
                    confirmRename();
                }
            }}
        />
    );
};
